/**
 * TanStack Query hooks for `conversion-pull-endpoint`. Thin wrappers over ./api.
 * Query keys: ['conversion-pull-endpoint'] list / ['conversion-pull-endpoint', id] detail.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	conversionPullEndpointQueryKeys,
	createConversion,
	getStats,
	listConversions,
	removeConversion,
	type ConversionRow,
	type CreateConversionBody,
	type ListConversionsParams,
	type ListConversionsResponse,
	type StatsResponse,
} from "./api";

export function useConversionPullEndpointList(
	params?: ListConversionsParams,
): UseQueryResult<ListConversionsResponse, Error> {
	return useQuery({
		queryKey: conversionPullEndpointQueryKeys.list(params),
		queryFn: () => listConversions(params),
	});
}

export function useConversionPullEndpointStats(): UseQueryResult<StatsResponse, Error> {
	return useQuery({
		queryKey: conversionPullEndpointQueryKeys.stats(),
		queryFn: getStats,
	});
}

export function useConversionPullEndpointCreate(): UseMutationResult<
	ConversionRow,
	Error,
	CreateConversionBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createConversion,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: conversionPullEndpointQueryKeys.all });
		},
	});
}

export function useConversionPullEndpointDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: removeConversion,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: conversionPullEndpointQueryKeys.all });
		},
	});
}
